import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Star,
  User,
  DollarSign,
  Users,
  TrendingUp,
} from 'lucide-react';
import { usePartner } from '../contexts/PartnerContext';
import BlurredAmount from './BlurredAmount';

const Dashboard: React.FC = () => {

  const { partner, partnerDashboard, loading, refreshProfile, refreshPartnerDashboard, refreshCommissions } = usePartner();

  useEffect(() => {
    const interval = setInterval(() => {
      refreshProfile();
      refreshPartnerDashboard();
      refreshCommissions();
    }, 30000);

    return () => clearInterval(interval);
  }, [refreshProfile, refreshPartnerDashboard, refreshCommissions]);

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'bronze': return 'text-amber-500';
      case 'silver': return 'text-gray-300';
      case 'gold': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  const getTierCommission = (tier: string) => {
    switch (tier) {
      case 'bronze': return 3;
      case 'silver': return 5;
      case 'gold': return 10;
      default: return 3;
    }
  };

  if (loading || !partner) {
    return (
      <div className="min-h-screen bg-tesla-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-tesla-black">
      {/* Main Content */}
      <div className="pt-20 max-w-6xl mx-auto px-6 py-8">
        
        {/* Compact Header with Profile and Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="tesla-card p-4 mb-6"
        >
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-center">
            
            {/* Profile Info */}
            <div className="text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start space-x-3 mb-2">
                <div className="w-12 h-12 bg-gradient-to-br from-tesla-gray to-gray-900 rounded-full flex items-center justify-center">
                  <User size={20} className="text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">{partner.name}</h1>
                  <div className="flex items-center space-x-1">
                    <Star size={12} className={getTierColor(partner.tier)} />
                    <span className={`text-xs font-medium ${getTierColor(partner.tier)} uppercase tracking-wider`}>
                      {partner.tier}
                    </span>
                    <span className="text-tesla-gray text-xs">•</span>
                    <span className="text-white text-xs">{getTierCommission(partner.tier)}%</span>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-2 text-xs">
                <div>
                  <p className="text-tesla-gray text-xs uppercase tracking-wider">ID Партнера</p>
                  <p className="text-white font-mono">{partner._id}</p>
                </div>
                <div>
                  <p className="text-tesla-gray text-xs uppercase tracking-wider">ID Пользователя</p>
                  <p className="text-white font-mono">{partner.userId}</p>
                </div>
              </div>
            </div>

            {/* Partner Stats */}
            <div className="lg:col-span-3 grid grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="tesla-glass p-3 rounded-lg text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <DollarSign size={16} className="text-green-400" />
                  <span className="text-tesla-gray text-xs uppercase tracking-wider">Баланс</span>
                </div>
                <BlurredAmount 
                  amount={partnerDashboard ? partnerDashboard.balance.toString() : partner.balance || '0'}
                  className="text-lg font-bold text-white"
                  formatCurrency={true}
                />
              </div>
              
              <div className="tesla-glass p-3 rounded-lg text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <TrendingUp size={16} className="text-blue-400" />
                  <span className="text-tesla-gray text-xs uppercase tracking-wider">Общие депозиты</span>
                </div>
                <BlurredAmount 
                  amount={partnerDashboard ? partnerDashboard.totalDepositAmount.toString() : '0'}
                  className="text-lg font-bold text-white"
                  formatCurrency={true}
                />
              </div>
              
              <div className="tesla-glass p-3 rounded-lg text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <Star size={16} className="text-yellow-400" />
                  <span className="text-tesla-gray text-xs uppercase tracking-wider">
                    {partnerDashboard?.isMaxTier ? 'Максимальный уровень' : 'Следующий уровень'}
                  </span>
                </div>
                <div className="text-lg font-bold text-white capitalize">
                  {partnerDashboard?.isMaxTier 
                    ? partnerDashboard.currentTier 
                    : (partnerDashboard?.nextTier || 'N/A')
                  }
                </div>
                {!partnerDashboard?.isMaxTier && partnerDashboard?.nextTier && (
                  <div className="text-xs text-tesla-gray">
                    {getTierCommission(partnerDashboard.nextTier)}% комиссия
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>


        {/* Прогресс уровня */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="tesla-card p-4 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <Star size={18} className={`mr-2 ${getTierColor(partner.tier)}`} />
              Уровень партнера
            </h3>
            <div className="text-right">
              <div className={`text-sm font-bold uppercase tracking-wider ${getTierColor(partner.tier)}`}>
                {partner.tier}
              </div>
              <div className="text-xs text-tesla-gray">
                {getTierCommission(partner.tier)}% комиссия
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="tesla-glass p-3 rounded-lg text-center">
              <div className="text-tesla-gray text-xs mb-1">Текущий уровень</div>
              <div className={`text-lg font-bold ${getTierColor(partnerDashboard ? partnerDashboard.currentTier : partner.tier)} capitalize`}>
                {partnerDashboard ? partnerDashboard.currentTier : partner.tier}
              </div>
              <div className="text-xs text-tesla-gray">
                {getTierCommission(partnerDashboard ? partnerDashboard.currentTier : partner.tier)}% комиссия
              </div>
            </div>

            

          </div>

          {/* Прогресс-бар для следующего уровня */}
          {partnerDashboard && !partnerDashboard.isMaxTier && (
            <div className="mt-4">
              <div className="flex justify-between text-xs text-tesla-gray mb-2">
                <span className="capitalize">{partnerDashboard.currentTier}</span>
                {partnerDashboard.nextTier && (
                  <span className="capitalize">{partnerDashboard.nextTier} ({getTierCommission(partnerDashboard.nextTier)}%)</span>
                )}
              </div>
              <div className="w-full bg-gray-800 rounded-full h-2 relative">
                <div 
                  className={`h-2 rounded-full transition-all duration-700 ease-out ${
                    partnerDashboard.currentTier === 'bronze' ? 'bg-gradient-to-r from-amber-400 to-amber-600' :
                    partnerDashboard.currentTier === 'silver' ? 'bg-gradient-to-r from-gray-300 to-gray-500' :
                    'bg-gradient-to-r from-yellow-400 to-yellow-600'
                  }`}
                  style={{ 
                    width: `${partnerDashboard.progressToNext || 0}%`
                  }}
                ></div>
                {/* Маркеры уровней */}
                <div className="absolute top-0 left-1/3 w-0.5 h-2 bg-gray-600"></div>
                <div className="absolute top-0 left-2/3 w-0.5 h-2 bg-gray-600"></div>
              </div>
              {partnerDashboard.remainingToNext && (
                <div className="text-xs text-tesla-gray mt-2 text-center">
                  Осталось: <BlurredAmount amount={partnerDashboard.remainingToNext.toString()} formatCurrency={true} className="text-white" />
                </div>
              )}
              
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;